import { useState, useEffect } from 'react';
import { Phone, Clock, User, Play, Loader2, ChevronDown, X, MessageSquare, Shield, CheckCircle, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';

const statusConfig = {
  in_progress: { label: 'In Progress', bg: 'bg-blue-50', text: 'text-[#2563eb]', dot: 'bg-[#2563eb]' },
  completed: { label: 'Completed', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  scheduled: { label: 'Scheduled', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  failed: { label: 'Failed', bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
};

export default function CallsPage() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [playbackCall, setPlaybackCall] = useState(null);
  const [playbackData, setPlaybackData] = useState(null);
  const [loadingPlayback, setLoadingPlayback] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCalls = async () => {
      try {
        const { data } = await api.get('/calls');
        setCalls(data || []);
      } catch (err) { logger.error(err); }
      finally { setLoading(false); }
    };
    fetchCalls();
  }, []);

  const loadPlayback = async (callId) => {
    setPlaybackCall(callId);
    setLoadingPlayback(true);
    try {
      const { data } = await api.get(`/calls/${callId}/playback`);
      setPlaybackData(data);
    } catch (err) { toast.error('Failed to load session'); }
    finally { setLoadingPlayback(false); }
  };

  const filters = ['all', 'in_progress', 'completed', 'scheduled'];
  const filtered = calls.filter(c => filter === 'all' || c.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <Loader2 className="w-8 h-8 text-[#2563eb] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8 bg-slate-50 min-h-full" data-testid="calls-page" style={{ fontFamily: 'Georgia, serif' }}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-800">Screenings</h1>
        <p className="text-slate-500 text-sm mt-1">AI interview sessions and their results</p>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 mb-6 bg-white border border-slate-200 rounded-xl p-1 w-fit">
        {filters.map(f => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${
              filter === f ? 'bg-[#2563eb] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            {f === 'all' ? `All (${calls.length})` : `${f.replace('_', ' ')} (${calls.filter(c => c.status === f).length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-2xl">
          <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No screening sessions yet</p>
          <p className="text-slate-400 text-xs mt-1">Shortlist candidates and start screenings to see sessions here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(call => {
            const st = statusConfig[call.status] || statusConfig.in_progress;
            return (
              <div key={call.call_id} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-sm transition-all" data-testid={`call-row-${call.call_id}`}>
                <div className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#2563eb]/10 border border-[#2563eb]/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#2563eb]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{call.candidate_name || 'Candidate'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs text-slate-400">{call.job_title || 'Position'}</p>
                        {call.duration_seconds > 0 && (
                          <>
                            <span className="text-slate-200">·</span>
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {Math.round(call.duration_seconds / 60)} min
                            </p>
                          </>
                        )}
                        {call.scheduled_at && (
                          <>
                            <span className="text-slate-200">·</span>
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> {new Date(call.scheduled_at).toLocaleDateString()}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {call.proctoring_score !== undefined && (
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Proctoring</p>
                        <p className={`text-sm font-bold ${
                          call.proctoring_score >= 80 ? 'text-emerald-600' :
                          call.proctoring_score >= 50 ? 'text-amber-600' : 'text-red-500'
                        }`}>{call.proctoring_score}%</p>
                      </div>
                    )}
                    <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                      {st.label}
                    </span>
                    {call.status === 'in_progress' && (
                      <button
                        data-testid={`resume-screening-${call.call_id}`}
                        onClick={() => navigate(`/screening/${call.call_id}`)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg text-xs font-medium transition-colors"
                      >
                        <Play className="w-3 h-3" /> Resume
                      </button>
                    )}
                    {call.status === 'completed' && (
                      <button
                        data-testid={`view-playback-${call.call_id}`}
                        onClick={() => loadPlayback(call.call_id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-medium transition-colors"
                      >
                        <MessageSquare className="w-3 h-3" /> Playback
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Playback Modal */}
      {playbackCall && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setPlaybackCall(null); setPlaybackData(null); }}>
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl p-6 max-h-[80vh] overflow-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="playback-modal">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Session Playback</h2>
                {playbackData && <p className="text-xs text-slate-400">{playbackData.candidate_name} · {playbackData.job_title}</p>}
              </div>
              <button onClick={() => { setPlaybackCall(null); setPlaybackData(null); }} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            {loadingPlayback ? (
              <div className="text-center py-10"><Loader2 className="w-8 h-8 text-[#2563eb] animate-spin mx-auto" /></div>
            ) : playbackData?.messages?.length > 0 ? (
              <div className="space-y-3">
                {playbackData.messages.map((msg, i) => (
                  <div key={msg.id || `${msg.role}-${i}`} className={`flex gap-3 ${msg.role === 'assistant' ? '' : 'flex-row-reverse'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'assistant' ? 'bg-[#2563eb]/10 text-[#2563eb]' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {msg.role === 'assistant' ? <Shield className="w-4 h-4" /> : <User className="w-4 h-4" />}
                    </div>
                    <div className={`max-w-sm rounded-2xl px-4 py-3 text-sm ${
                      msg.role === 'assistant' ? 'bg-[#2563eb] text-white' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 text-slate-400">No messages recorded for this session</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
